import React from "react"
import { Box, Stack } from "@mui/material"
import { ViraButton } from "../atoms/ViraButton"
import { LoadingBar } from "../atoms/LoadingBar"
import { ViraLink } from "../atoms/ViraLink"
import { ViraIcon } from "../atoms/ViraIcon"
import { ViraText } from "../atoms/ViraText"
import {
  InputStatusRow,
  type InputStatusItem,
} from "../molecules/InputStatusRow"
import { EmptyState } from "../molecules/EmptyState"
import { TechniqueListItem } from "../molecules/TechniqueListItem"
import { ContentCard } from "../molecules/ContentCard"
import { SectionHeader } from "../molecules/SectionHeader"
import { TheirWeek, type TheirWeekEntry } from "../molecules/TheirWeek"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type PlanAndNoteVariant =
  | "no_inputs"
  | "manually_create"
  | "data_transcript_plan_in_progress"
  | "data_transcript_plan_complete"
  | "no_data_transcript_plan_in_progress"
  | "data_no_transcript_plan_in_progress"
  | "action_plan_drafted_not_approved"

export type PlanItemSection = "from_session" | "vira_suggests" | "due_now"

export type PlanItem = {
  id: string
  title: string
  detail?: string
  /** When this nudge will be delivered. */
  cadence?: string
  approved?: boolean
  rejected?: boolean
  disabled?: boolean
  /** Best-effort grouping for Figma sections. Defaults to vira_suggests. */
  section?: PlanItemSection
  /** Expanded detail under the row (goal editor, contingencies). */
  expandedContent?: React.ReactNode
}

export type PlanAndNotePanelProps = {
  variant: PlanAndNoteVariant
  clientName: string
  inputs?: InputStatusItem[]
  readyCount?: number
  totalCount?: number
  planItems?: PlanItem[]
  planStatusLabel?: string
  noteStatus?: "locked" | "drafting" | "ready"
  notePreview?: string
  /**
   * Page-level eyebrow + title. When embedded under Client Detail /
   * `ClientDetailPage` chrome, pass `false` so the shared pane header is used.
   */
  showPageHeader?: boolean
  /** Extra actions beside “Add a nudge” (e.g. Generate / Regenerate). */
  planHeaderActions?: React.ReactNode
  /** Footer below the plan card (e.g. Sign approved items). */
  footer?: React.ReactNode
  /** Optional “Their week” sidebar. */
  theirWeekEntries?: TheirWeekEntry[]
  theirWeekSubtitle?: string
  /** Replace the built-in draft-note card (e.g. ProgressNoteCard). */
  noteSlot?: React.ReactNode
  /** Hide the Session note card (e.g. when note lives on its own tab). */
  hideNoteCard?: boolean
  /** Hide the Session Status row (e.g. when lifted above session tabs). */
  showInputs?: boolean
  /** Intro sentence above tab actions (Transcript-tab layout). */
  intro?: string
  /** Persistent actions under the intro (e.g. Auto generate). */
  tabActions?: React.ReactNode
  errorMessage?: string
  onRecordRecap?: (event?: React.MouseEvent<HTMLElement>) => void
  onUploadTranscript?: () => void
  onBuildPlanMyself?: () => void
  /** @deprecated Prefer tabActions + onGenerate via parent. Kept for callers. */
  onGeneratePlan?: () => void
  generatePlanDisabled?: boolean
  onDraftNote?: () => void
  onApproveItem?: (id: string) => void
  onRejectItem?: (id: string) => void
  onCustomizeItem?: (id: string) => void
  onRemoveItem?: (id: string) => void
  onAddTechnique?: () => void
  /** When true, use Add / Leave out / Customize / Remove labels. */
  loopLabels?: boolean
}

const EMPTY_INPUTS: InputStatusItem[] = [
  {
    id: "app",
    title: "App data",
    status: "missing",
    detail: "Janice hasn’t opened the app",
    actionLabel: "Check settings →",
    tooltip:
      "Signals from the client’s Vira mobile app—sleep, activity, mood check-ins, and diary cards when enabled. Pulled from their linked app account for this client (most recent 7 days of daily features); used to ground the action plan and progress note.",
  },
  {
    id: "session",
    title: "Session record",
    status: "missing",
    detail: "Nothing recorded",
    actionLabel: "Record a recap →",
  },
  {
    id: "plan",
    title: "Action plan",
    status: "missing",
    detail: "Not drafted",
    actionLabel: "Build it myself →",
  },
  {
    id: "note",
    title: "Note",
    status: "missing",
    detail: "Not drafted",
  },
]

const DEFAULT_PLAN: PlanItem[] = [
  {
    id: "1",
    title: "Urge surfing - 5 minutes after dinner",
    detail: "Recurring · evenings",
    approved: false,
    section: "from_session",
  },
  {
    id: "2",
    title: "Opposite action when loneliness spikes",
    detail: "One time · this week",
    approved: false,
    section: "vira_suggests",
  },
]

const SECTION_ORDER: Array<{ id: PlanItemSection; label: string }> = [
  { id: "from_session", label: "From your session" },
  { id: "vira_suggests", label: "Vira suggests" },
  { id: "due_now", label: "Due now" },
]

/**
 * Post-session action plan + optional progress note layout (Loop Pro and legacy
 * Post Session tab). Uses intro → tab actions → plan card pattern.
 */
export function PlanAndNotePanel({
  variant,
  clientName,
  inputs = EMPTY_INPUTS,
  readyCount,
  totalCount = 4,
  planItems = DEFAULT_PLAN,
  planStatusLabel,
  noteStatus = "locked",
  notePreview,
  showPageHeader = true,
  planHeaderActions,
  footer,
  theirWeekEntries,
  theirWeekSubtitle,
  noteSlot,
  hideNoteCard = false,
  showInputs = true,
  intro,
  tabActions,
  errorMessage,
  onGeneratePlan,
  generatePlanDisabled = false,
  onDraftNote,
  onApproveItem,
  onRejectItem,
  onCustomizeItem,
  onRemoveItem,
  onAddTechnique,
  loopLabels = true,
}: PlanAndNotePanelProps) {
  const computedReady =
    readyCount ?? inputs.filter((i) => i.status === "ready").length
  // Empty hero is retired for Loop Pro session tabs — callers always pass
  // intro + tabActions. Keep the prop path for Storybook-only demos.
  const showEmptyHero = variant === "no_inputs" && !intro && !tabActions
  const showPlan = variant !== "no_inputs" || Boolean(intro || tabActions)
  const showPlanCard =
    showPlan &&
    (variant === "manually_create" ||
      variant.includes("plan") ||
      variant === "action_plan_drafted_not_approved")
  const inProgress = variant.includes("in_progress")
  const complete = variant.includes("complete")
  const [expandedId, setExpandedId] = React.useState<string | null>(null)

  const approveLabel = loopLabels ? "Add" : "Approve"
  const rejectLabel = loopLabels ? "Leave out" : "Reject"
  const approvedStatusLabel = loopLabels ? "Added" : "Approved"
  const rejectedStatusLabel = loopLabels ? "Left out" : "Rejected"

  const visibleItems = planItems.filter((item) => !item.rejected)
  const grouped = SECTION_ORDER.map((section) => ({
    ...section,
    items: visibleItems.filter(
      (item) => (item.section ?? "vira_suggests") === section.id,
    ),
  })).filter((section) => section.items.length > 0)

  const showTheirWeek = theirWeekEntries !== undefined
  const addedCount = planItems.filter((item) => item.approved).length
  const proposedCount = planItems.filter(
    (item) => !item.approved && !item.rejected,
  ).length

  const renderItem = (item: PlanItem) => {
    const isExpanded = expandedId === item.id
    const handleCustomize = () => {
      if (onCustomizeItem) {
        onCustomizeItem(item.id)
        return
      }
      if (item.expandedContent) {
        setExpandedId((current) => (current === item.id ? null : item.id))
      }
    }
    return (
      <TechniqueListItem
        key={item.id}
        title={item.title}
        detail={item.detail}
        cadence={item.cadence}
        approved={item.approved}
        rejected={item.rejected}
        disabled={item.disabled}
        approveLabel={approveLabel}
        rejectLabel={rejectLabel}
        approvedStatusLabel={approvedStatusLabel}
        rejectedStatusLabel={rejectedStatusLabel}
        onApprove={
          onApproveItem && !item.approved
            ? () => onApproveItem(item.id)
            : undefined
        }
        onReject={
          onRejectItem && !item.approved && !item.rejected
            ? () => onRejectItem(item.id)
            : undefined
        }
        onCustomize={
          item.expandedContent || onCustomizeItem ? handleCustomize : undefined
        }
        onRemove={
          item.approved && onRemoveItem
            ? () => onRemoveItem(item.id)
            : item.approved && onRejectItem
              ? () => onRejectItem(item.id)
              : undefined
        }
      >
        {/* Contigencies / extras under the row (not toggled by Customize when modal handles edits). */}
        {item.expandedContent && (onCustomizeItem ? true : isExpanded)
          ? item.expandedContent
          : null}
      </TechniqueListItem>
    )
  }

  const planCard = showPlanCard ? (
    <ContentCard
      title="Action plan"
      meta={
        <ViraText tone="subtle" sx={{ fontSize: 12 }}>
          {planStatusLabel ??
            (complete
              ? approvedStatusLabel
              : inProgress
                ? "Drafted, not approved"
                : "In progress")}
        </ViraText>
      }
      actions={
        planHeaderActions ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {planHeaderActions}
          </Stack>
        ) : undefined
      }
      sx={{ mb: 3, maxWidth: "100%" }}
    >
      {inProgress && planItems.length === 0 ? (
        <LoadingBar label="Drafting action plan…" />
      ) : (
        <Stack spacing={2.5}>
          {grouped.length > 0 ? (
            grouped.map((section) => (
              <Box key={section.id}>
                <ViraText
                  tone="muted"
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  {section.label}
                </ViraText>
                <Stack spacing={1.5}>{section.items.map(renderItem)}</Stack>
              </Box>
            ))
          ) : (
            <Stack spacing={1.5}>{visibleItems.map(renderItem)}</Stack>
          )}
          {onAddTechnique ? (
            <ViraLink onClick={onAddTechnique} sx={{ alignSelf: "flex-start" }}>
              + Add a nudge
            </ViraLink>
          ) : null}
          {!inProgress && visibleItems.length === 0 && !onAddTechnique ? (
            <ViraText tone="muted" sx={{ fontSize: 14 }}>
              No plan items yet. Add a nudge below, or auto-generate a draft.
            </ViraText>
          ) : null}
        </Stack>
      )}
    </ContentCard>
  ) : null

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        overflowX: "hidden",
        // Standalone / Storybook: own column. Under Client Detail `ClientDetailPage`,
        // fill the shared pane width.
        maxWidth: showPageHeader
          ? { xs: "100%", lg: showTheirWeek ? 1100 : 1028 }
          : "100%",
      }}
    >
      {showPageHeader ? (
        <SectionHeader
          level="page"
          eyebrow={`${clientName} · After the session`}
          title="Plan and note"
        />
      ) : null}

      {showInputs ? (
        <Box sx={{ mb: 3, maxWidth: "100%", minWidth: 0, overflowX: "clip" }}>
          <InputStatusRow
            items={inputs}
            readyCount={computedReady}
            totalCount={totalCount}
          />
        </Box>
      ) : null}

      {errorMessage ? (
        <Box
          role="alert"
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "4px",
            backgroundColor: "#FFDBD9",
            color: "#E23E4F",
            fontSize: 14,
            lineHeight: 1.5,
            width: "100%",
          }}
        >
          {errorMessage}
        </Box>
      ) : null}

      {showEmptyHero ? (
        <ContentCard
          accentTop
          sx={{
            maxWidth: { xs: "100%", md: 750 },
            mx: "auto",
            mb: 3,
          }}
        >
          <EmptyState
            title="Nothing to work from yet"
            description="Open a completed session to build or generate an action plan."
            icon="plan"
          />
        </ContentCard>
      ) : null}

      {!showEmptyHero && (intro || tabActions || onGeneratePlan) ? (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            justifyItems: "start",
            mb: 3,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          {intro ? (
            <ViraText tone="muted" sx={{ fontSize: 14 }}>
              {intro}
            </ViraText>
          ) : null}
          {tabActions ? (
            <Box sx={{ width: "100%", justifySelf: "stretch" }}>
              {tabActions}
            </Box>
          ) : onGeneratePlan ? (
            <ViraButton
              variant="primary"
              disabled={generatePlanDisabled}
              onClick={() => onGeneratePlan()}
              sx={{ minHeight: 40 }}
            >
              Auto generate the plan
            </ViraButton>
          ) : null}
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 3,
          alignItems: "flex-start",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: "100%", maxWidth: "100%" }}>
          {planCard}

          {footer ? <Box sx={{ mb: 3, maxWidth: "100%" }}>{footer}</Box> : null}

          {hideNoteCard ? null : noteSlot ? (
            noteSlot
          ) : (
            <ContentCard
              title="Session note"
              titleStart={
                <Box
                  sx={(theme) => ({
                    width: 34,
                    height: 34,
                    borderRadius: "9px",
                    backgroundColor: resolveViraPalette(theme).softSurface,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  })}
                >
                  <ViraIcon name="note" tone="subtle" sx={{ fontSize: 18 }} />
                </Box>
              }
              actions={
                <ViraButton
                  variant="secondary"
                  disabled={noteStatus === "locked"}
                  onClick={onDraftNote}
                  sx={{ minHeight: 40 }}
                >
                  Draft the note
                </ViraButton>
              }
              sx={(theme) => ({
                maxWidth: "100%",
                minWidth: 0,
                backgroundColor:
                  noteStatus === "locked"
                    ? resolveViraPalette(theme).pageBg
                    : theme.palette.background.paper,
              })}
            >
              <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                {noteStatus === "locked"
                  ? "Nothing to build a note from yet. Record a recap or add an action plan, and this unlocks."
                  : noteStatus === "drafting"
                    ? "Drafting from client data, transcript, and plan."
                    : (notePreview ?? "Draft ready for review.")}
              </ViraText>
              {noteStatus === "drafting" ? (
                <Box sx={{ mt: 1 }}>
                  <LoadingBar label="" />
                </Box>
              ) : null}
            </ContentCard>
          )}
        </Box>

        {showTheirWeek ? (
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", lg: 280 },
              maxWidth: "100%",
              minWidth: 0,
            }}
          >
            <TheirWeek
              collapsible
              defaultExpanded={false}
              subtitle={
                theirWeekSubtitle ??
                `${addedCount} of ${addedCount + proposedCount} added`
              }
              entries={theirWeekEntries}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
